import {
  APP_NAME,
  type CheckoutResponse,
  CREDIT_COPY,
  CREDIT_TERMS_VERSION,
  dollars,
  type MeResponse,
  TEXT_PACKS,
  TEXT_PRICE_CENTS,
  TEXTS_COMING_SOON,
  WELCOME_TEXTS,
} from '@eventping/shared'
import { Link } from '@tanstack/react-router'
import { ArrowRight, CreditCard, Gift, Info, Mail, MessageSquareText } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { Card, FieldError, Footer, Page, Wordmark } from '@/components/shell'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { ApiFailure, api, useMe } from '@/lib/api'
import { cn } from '@/lib/utils'
import { CreatorHeader } from './home'

/**
 * /pricing (docs/PRICING.md §1, §6.5): email is free, texts are 2¢ each from prepaid packs.
 * Signed in, the buy flow: pick a pack, tick the required no-refund box, go to Stripe
 * Checkout, come back to the text log. Without Stripe configured the buttons say "coming
 * soon". The California sales tax note shows only while TAX_MODE=stripe.
 *
 * Texts-off mode (MeResponse.textsEnabled false): a coming-soon page. Email is free; no packs,
 * no checkout, no tax line, and only the email questions.
 */
export type PricingSearch = { checkout?: string }

const FAQ = [
  {
    q: 'What counts as a text?',
    a: `Every text ${APP_NAME} sends for you is one short message, so one text uses one credit: a person’s sign-up confirmation, and each Notify step marked done. Replies people send, and our answers to STOP, HELP and LINK, are free.`,
  },
  {
    q: 'What happens when I run out?',
    a: 'Texts pause, and everyone keeps getting your updates by email. Buy more texts and texting turns back on at once. Updates sent while you were out aren’t re-sent as texts: they already went by email.',
  },
  {
    q: 'Can I get a refund?',
    a: 'No. Text credit is non-refundable, for packs and the welcome credit. In exchange, it never expires. Texts that fail to send are credited back automatically.',
  },
  {
    q: 'Can I move credit to someone else?',
    a: 'No. Credit stays on the account that bought or received it, and you can use it on any of your events.',
  },
  {
    q: 'Do I need texts at all?',
    a: 'No. Email updates are free and unlimited. Texts are for the people who’d rather get a text.',
  },
] as const

/** Texts-off mode's questions: email only. */
const FAQ_EMAIL = [
  {
    q: 'Is email really free?',
    a: 'Yes. Email updates are free and unlimited, on every event.',
  },
  {
    q: 'When are text updates coming?',
    a: 'Soon. Until then, everyone who signs up gets every update by email, with a link to the live checklist.',
  },
  {
    q: 'Do people need an account to get updates?',
    a: 'No. They sign up on your event’s join page with their email address. No account or password.',
  },
] as const

export function PricingPage({ search }: { search: PricingSearch }) {
  const me = useMe()
  if (!me.data) return <Page>{null}</Page>
  const signedIn = Boolean(me.data.user)
  const tax = me.data.credit.tax
  const texts = me.data.textsEnabled
  return (
    <Page
      header={
        signedIn ? (
          <CreatorHeader me={me.data} />
        ) : (
          <>
            <Link to="/">
              <Wordmark />
            </Link>
            <Button asChild variant="ghost" size="sm" className="ml-auto">
              <a href={me.data.auth === 'oidc' ? '/auth/login?returnTo=%2Fpricing' : '/#sign-in'}>
                Sign in
              </a>
            </Button>
          </>
        )
      }
    >
      {texts ? (
        <>
          <section className="pt-2 pb-6">
            <h1 className="text-3xl leading-tight font-semibold text-balance sm:text-4xl">
              {CREDIT_COPY.headline}
            </h1>
            <p className="mt-3 text-lg text-muted-foreground">{CREDIT_COPY.sub}</p>
            {tax && (
              <p
                className="mt-3 flex gap-2 rounded-xl bg-muted px-4 py-3 text-sm"
                data-testid="tax-note"
              >
                <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
                {CREDIT_COPY.taxNote}
              </p>
            )}
          </section>

          <section aria-labelledby="model" className="mb-6 grid gap-3 sm:grid-cols-3">
            <h2 id="model" className="sr-only">
              How it works
            </h2>
            {[
              {
                icon: Mail,
                title: 'Email: free',
                text: 'Unlimited email updates. Everyone who signs up gives an email.',
              },
              {
                icon: Gift,
                title: `${WELCOME_TEXTS} free texts`,
                text: CREDIT_COPY.welcome,
              },
              {
                icon: MessageSquareText,
                title: `Texts: ${TEXT_PRICE_CENTS}¢ each`,
                text: 'Paid from your credit, on any event. Credit never expires.',
              },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="rounded-2xl bg-card p-4 shadow-xs">
                <Icon className="mb-2 size-6 text-primary" aria-hidden />
                <p className="font-semibold">{title}</p>
                <p className="mt-1 text-sm text-muted-foreground">{text}</p>
              </div>
            ))}
          </section>

          {search.checkout === 'canceled' && (
            <p
              role="status"
              data-testid="checkout-canceled"
              className="mb-4 rounded-xl bg-muted px-4 py-3 text-sm"
            >
              Checkout canceled. Nothing was charged.
            </p>
          )}

          <BuyTexts me={me.data} />

          <section aria-labelledby="faq" className="mt-8">
            <h2 id="faq" className="mb-3 text-xl font-semibold">
              Questions
            </h2>
            <Accordion type="single" collapsible className="rounded-2xl bg-card px-4 shadow-xs">
              {FAQ.map(({ q, a }) => (
                <AccordionItem key={q} value={q}>
                  <AccordionTrigger>{q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <p className="mt-4 text-sm">
              <Link to="/terms" hash="text-credit" className="underline">
                Text credit terms
              </Link>
            </p>
          </section>
        </>
      ) : (
        <ComingSoon />
      )}
      <Footer />
    </Page>
  )
}

/** Texts-off mode: email is free, texts are coming soon. */
function ComingSoon() {
  return (
    <>
      <section className="pt-2 pb-6" data-testid="pricing-coming-soon">
        <h1 className="text-3xl leading-tight font-semibold text-balance sm:text-4xl">
          Email updates are free. {TEXTS_COMING_SOON}
        </h1>
        <p className="mt-3 text-lg text-muted-foreground">
          Every update goes out by email, free and unlimited, on every event. Text updates will be
          an optional extra when they arrive.
        </p>
      </section>
      <section aria-labelledby="model" className="mb-6">
        <h2 id="model" className="sr-only">
          How it works
        </h2>
        <div className="rounded-2xl bg-card p-4 shadow-xs">
          <Mail className="mb-2 size-6 text-primary" aria-hidden />
          <p className="font-semibold">Email: free</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Unlimited email updates. Everyone who signs up gives an email.
          </p>
        </div>
      </section>
      <section aria-labelledby="faq" className="mt-8">
        <h2 id="faq" className="mb-3 text-xl font-semibold">
          Questions
        </h2>
        <Accordion type="single" collapsible className="rounded-2xl bg-card px-4 shadow-xs">
          {FAQ_EMAIL.map(({ q, a }) => (
            <AccordionItem key={q} value={q}>
              <AccordionTrigger>{q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </>
  )
}

function BuyTexts({ me }: { me: MeResponse }) {
  const [pack, setPack] = useState<string>(TEXT_PACKS[1]?.id ?? '')
  const [agreed, setAgreed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<{ field?: string; message: string } | null>(null)
  const signedIn = Boolean(me.user)
  const open = me.credit.checkout
  const chosen = TEXT_PACKS.find((p) => p.id === pack)

  async function buy(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!agreed) {
      setError({
        field: 'terms',
        message: 'Tick the box to accept the text credit terms. Text credit is non-refundable.',
      })
      return
    }
    setBusy(true)
    try {
      const { url } = await api<CheckoutResponse>('/api/account/checkout', {
        body: { pack, acceptTerms: true, termsVersion: CREDIT_TERMS_VERSION },
      })
      window.location.assign(url)
    } catch (err) {
      setError(
        err instanceof ApiFailure
          ? { field: err.field, message: err.message }
          : { message: 'Something went wrong. Try again.' },
      )
      setBusy(false)
    }
  }

  return (
    <Card id="buy" className="scroll-mt-20">
      <h2 className="mb-1 text-lg font-semibold">Buy texts</h2>
      <p className="mb-4 text-sm text-muted-foreground">
        One-time payment, no subscription, no stored card. We take care of the card fees.
      </p>
      {!open && (
        <p
          className="mb-4 flex gap-2 rounded-xl bg-muted px-4 py-3 text-sm"
          data-testid="checkout-soon"
        >
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
          Buying texts is coming soon. Every account starts with {WELCOME_TEXTS} free texts, and
          email updates are always free.
        </p>
      )}
      <form onSubmit={buy} noValidate className="space-y-4">
        <fieldset className="grid gap-3 sm:grid-cols-3" disabled={!open || !signedIn}>
          <legend className="sr-only">Pick a pack</legend>
          {TEXT_PACKS.map((p) => (
            <label
              key={p.id}
              data-testid={`pack-${p.texts}`}
              className={cn(
                'flex cursor-pointer flex-col rounded-2xl border p-4 transition-colors has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60',
                pack === p.id ? 'border-primary bg-accent' : 'bg-card hover:bg-accent/60',
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-2xl font-semibold">{dollars(p.cents)}</span>
                <input
                  type="radio"
                  name="pack"
                  value={p.id}
                  checked={pack === p.id}
                  onChange={() => setPack(p.id)}
                  className="size-5 accent-primary"
                  aria-label={`${p.texts.toLocaleString('en-US')} texts for ${dollars(p.cents)}`}
                />
              </span>
              <span className="font-medium">{p.texts.toLocaleString('en-US')} texts</span>
              <span className="text-sm text-muted-foreground">{TEXT_PRICE_CENTS}¢ each</span>
            </label>
          ))}
        </fieldset>

        {me.credit.tax && chosen && (
          <dl className="space-y-1 rounded-xl bg-muted px-4 py-3 text-sm" data-testid="tax-lines">
            <div className="flex justify-between gap-3">
              <dt>{chosen.texts.toLocaleString('en-US')} texts</dt>
              <dd className="tabular-nums">{dollars(chosen.cents)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt>{CREDIT_COPY.taxLine}</dt>
              <dd>Added at checkout</dd>
            </div>
            <p className="pt-1 text-muted-foreground">{CREDIT_COPY.taxNote}</p>
          </dl>
        )}

        {signedIn ? (
          <>
            <div
              className={cn(
                'flex gap-3 rounded-2xl border p-4',
                error?.field === 'terms'
                  ? 'border-destructive'
                  : agreed && 'border-primary bg-accent',
              )}
            >
              <input
                id="credit-terms"
                type="checkbox"
                checked={agreed}
                disabled={!open}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 size-6 shrink-0 accent-primary"
                aria-invalid={error?.field === 'terms' || undefined}
              />
              <label htmlFor="credit-terms" className="text-[0.95rem] leading-relaxed">
                I understand text credit is <strong>non-refundable</strong> and can’t be transferred
                to another account. It never expires. Texts that fail to send are credited back
                automatically.{' '}
                <a
                  href="/terms#text-credit"
                  target="_blank"
                  rel="noopener"
                  className="font-medium underline"
                >
                  Text credit terms
                </a>
              </label>
            </div>
            {error && <FieldError>{error.message}</FieldError>}
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={!open || busy}
              data-testid="checkout-button"
            >
              <CreditCard />
              {open
                ? `Continue to checkout${chosen ? `, ${dollars(chosen.cents)}` : ''}`
                : 'Coming soon'}
            </Button>
            {open && (
              <p className="text-center text-xs text-muted-foreground">
                Payment is handled by Stripe. You’ll come back here when it’s done.
              </p>
            )}
          </>
        ) : (
          <Button asChild size="lg" className="w-full">
            <a href={me.auth === 'oidc' ? '/auth/login?returnTo=%2Fpricing' : '/#sign-in'}>
              Sign in to buy texts <ArrowRight />
            </a>
          </Button>
        )}
      </form>
    </Card>
  )
}
